class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();

    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);
    this.debouncedOnSubmit = debounce(event => {
      this.onSubmitHandler(event);
    }, 500);
    this.querySelector('form').addEventListener(
      'input',
      this.debouncedOnSubmit.bind(this)
    );
    this.btnClearFilters = this.querySelector('facet-remove')?.querySelector('a');
  }

  clearCheckboxes() {
    const checkboxes = this.querySelectorAll(
      'input[type="checkbox"]'
    );
    checkboxes.forEach(checkbox => {
      checkbox.checked = false;
    });
  }

  static setListeners() {
    const onHistoryChange = event => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    if (!document.querySelector('facet-filters-form')) return;
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sections = FacetFiltersForm.getSections();

    sections.forEach(section => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = element => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(
          filterDataUrl,
          event
        )
        : FacetFiltersForm.renderSectionFromFetch(url, event);

      if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
    });
  }

  static beginAjaxLoading() {
    clearTimeout(FacetFiltersForm._loadingTimeout);
    FacetFiltersForm._ajaxLoadingDepth = 1;
    document.querySelector('.js-header')?.classList.add('is-collection-filter-loading');
    document.querySelector('.collection-facets')?.classList.add('is-collection-filter-loading');
    let loader = document.querySelector('.collection-filter-loading-bar');
    if (!loader) {
      loader = document.createElement('div');
      loader.className = 'collection-filter-loading-bar';
      document.body.appendChild(loader);
    }
    const header = document.querySelector('.js-header');
    if (header) {
      loader.style.top = (header.offsetTop + header.offsetHeight) + 'px';
    }
    loader.classList.add('is-visible');
    FacetFiltersForm._loadingTimeout = setTimeout(function() {
      FacetFiltersForm.endAjaxLoading();
    }, 5000);
  }

  static endAjaxLoading() {
    FacetFiltersForm._ajaxLoadingDepth = 0;
    clearTimeout(FacetFiltersForm._loadingTimeout);
    document.querySelector('.js-header')?.classList.remove('is-collection-filter-loading');
    document.querySelector('.collection-facets')?.classList.remove('is-collection-filter-loading');
    const loader = document.querySelector('.collection-filter-loading-bar');
    if (loader) loader.classList.remove('is-visible');
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then(response => response.text())
      .then(responseText => {
        const html = responseText;
        FacetFiltersForm.filterData = [
          ...FacetFiltersForm.filterData,
          { html, url }
        ];
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderCollectionView(html);
        FacetFiltersForm.renderCategoryPills(html);
      })
      .catch(function() {})
      .finally(function() {
        FacetFiltersForm.endAjaxLoading();
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    var html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFilters(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
    FacetFiltersForm.renderCollectionView(html);
    FacetFiltersForm.renderCategoryPills(html);
    FacetFiltersForm.endAjaxLoading();
  }

  static renderCategoryPills(html) {
    const parsed =
      typeof html === 'string'
        ? new DOMParser().parseFromString(html, 'text/html')
        : html;
    const sourceWrap = parsed.querySelector('.collection-category-pills-wrap');
    const targetWrap = document.querySelector('.collection-category-pills-wrap');
    if (!sourceWrap || !targetWrap) return;
    const pillsNav = targetWrap.querySelector('.collection-category-pills');
    const savedScrollLeft = pillsNav ? pillsNav.scrollLeft : 0;
    targetWrap.innerHTML = sourceWrap.innerHTML;
    const newPillsNav = targetWrap.querySelector('.collection-category-pills');
    if (newPillsNav) {
      newPillsNav.scrollLeft = savedScrollLeft;
    }
    setTimeout(function() {
      const evt = new CustomEvent('collection:pills-updated');
      document.dispatchEvent(evt);
    }, 50);
  }

  static renderCollectionView(html) {
    const collectionView = document.querySelector('collection-view');
    const collectionViewParent = collectionView?.parentElement;

    if (!collectionView) {
      return;
    }

    const updatedCollectionView = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('collection-view');

    if (!updatedCollectionView) {
      return;
    }

    const updatedCollectionViewParent = updatedCollectionView.parentElement;
    const isHidden = updatedCollectionViewParent.classList.contains('hidden');

    isHidden ? collectionViewParent.classList.add('hidden') : collectionViewParent.classList.remove('hidden');
  }

  static renderProductGridContainer(html) {
    const productGridContainer = document.querySelector('#ProductGridContainer');
    const collectionView = document.querySelector('collection-view');

    const updatedProductGridContainer = new DOMParser()
      .parseFromString(html, 'text/html')
      .querySelector('#ProductGridContainer');

    if (collectionView) {
      const btnActive = collectionView.querySelector('.js-btn:disabled');
      const updatedProductGrid = updatedProductGridContainer.querySelector('#product-grid');

      updatedProductGrid ? updatedProductGrid.dataset.cols = btnActive.dataset.cols : '';
    }

    productGridContainer.innerHTML = updatedProductGridContainer.innerHTML;
    document.dispatchEvent(new CustomEvent('collection:grid-updated', { bubbles: true, detail: { source: 'facets' } }));
  }

  static renderProductCount(html) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');

    const syncCount = id => {
      const source = parsed.getElementById(id);
      const target = document.getElementById(id);

      if (!target) {
        return;
      }

      target.innerHTML = source ? source.innerHTML : '';
    };

    syncCount('ProductCount');
    syncCount('ProductCountMobile');
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(
      html,
      'text/html'
    );

    const triggeredJsFilter = event
      ? event.target.closest('.js-filter')
      : null;
    const triggeredForm = triggeredJsFilter
      ? triggeredJsFilter.closest('form')
      : null;
    const triggeredFormId = triggeredForm ? triggeredForm.id : null;
    const triggeredIndex = triggeredJsFilter
      ? triggeredJsFilter.dataset.index
      : null;

    const formIds = ['FacetFiltersForm', 'FacetFiltersFormAside'];

    formIds.forEach(formId => {
      parsedHTML.querySelectorAll(`#${formId} .js-filter[data-index]`).forEach(
        source => {
          const idx = source.dataset.index;
          if (
            triggeredFormId &&
            triggeredIndex != null &&
            formId === triggeredFormId &&
            idx === triggeredIndex
          ) {
            return;
          }
          document
            .querySelectorAll(`#${formId} .js-filter[data-index="${idx}"]`)
            .forEach(target => {
              target.innerHTML = source.innerHTML;
            });
        }
      );
    });

    FacetFiltersForm.renderActiveFacets(parsedHTML);

    if (triggeredJsFilter && triggeredFormId && triggeredIndex != null) {
      const sourceForCounts = parsedHTML.querySelector(
        `#${triggeredFormId} .js-filter[data-index="${triggeredIndex}"]`
      );
      if (sourceForCounts) {
        FacetFiltersForm.renderCounts(sourceForCounts, triggeredJsFilter);
      }
    }
  }

  static renderActiveFacets(html) {
    const activeFacetElementSelector = '.js-facets-active';
    const activeFacetsElement = html.querySelector(
      activeFacetElementSelector
    );

    if (!activeFacetsElement) return;
    document.querySelector(activeFacetElementSelector).outerHTML =
      activeFacetsElement.outerHTML;
  }

  static renderCounts(source, target) {
    const selector = '.js-facets-selected';
    const targetElement = target.querySelector(selector);
    const sourceElement = source.querySelector(selector);

    if (sourceElement && targetElement) {
      target.querySelector(selector).outerHTML =
        source.querySelector(selector).outerHTML;
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      '',
      `${window.location.pathname}${searchParams && '?'.concat(searchParams)
      }`
    );
  }

  static getSections() {
    return [
      {
        section: document.querySelector('facet-filters-form').dataset
          .sectionId
      }
    ];
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const context = event.target.id.includes('desktop')
      ? 'mobile'
      : 'desktop';
    const formData = new FormData();
    const formDataElements = Array.from(
      event.target.closest('form').elements
    ).filter(
      element =>
        !element.id.includes(context) || element.name === 'sort_by'
    );
    let sortByValue = null;

    for (const element of formDataElements) {
      if (!element.name) continue;
      if (element.type === 'checkbox') {
        if (element.checked) {
          formData.append(element.name, element.value);
        }
      } else {
        formData.append(element.name, element.value);

        if (element.name === 'sort_by') {
          sortByValue = element.value;
        }
      }
    }
    const searchParams = new URLSearchParams(formData).toString();
    FacetFiltersForm.renderPage(searchParams, event);

    if (!sortByValue) {
      return;
    }

    const sortParam = 'sort_by=';
    const sortValueIndex = this.btnClearFilters.href.indexOf(sortParam);
    let updatedBtnURL = this.btnClearFilters.href.slice(0, sortValueIndex + sortParam.length) + sortByValue;

    const currentSearch = new URLSearchParams(window.location.search);
    if (currentSearch.has('q')) {
      const qValue = currentSearch.get('q');
      if (!updatedBtnURL.includes('q=')) {
        updatedBtnURL = updatedBtnURL.replace('?', `?q=${qValue}&`);
      }
    }

    this.btnClearFilters.href = updatedBtnURL;
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url =
      event.currentTarget.href.indexOf('?') == -1
        ? ''
        : event.currentTarget.href.slice(
          event.currentTarget.href.indexOf('?') + 1
        );
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial =
  window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

(function() {
  function initPillsNavigation() {
    const pillsWrappers = document.querySelectorAll('.collection-category-pills-wrap');
    
    pillsWrappers.forEach(function(wrapper) {
      const pillsNav = wrapper.querySelector('.collection-category-pills');
      const leftArrow = wrapper.querySelector('.collection-category-pills__arrow--left');
      const rightArrow = wrapper.querySelector('.collection-category-pills__arrow--right');
      
      if (!pillsNav || !leftArrow || !rightArrow) return;
      
      const scrollAmount = 150;
      
      function updateArrowVisibility() {
        const scrollLeft = Math.round(pillsNav.scrollLeft);
        const maxScroll = pillsNav.scrollWidth - pillsNav.clientWidth;
        let anyVisible = false;
        
        if (scrollLeft <= 0) {
          leftArrow.classList.add('is-hidden');
        } else {
          leftArrow.classList.remove('is-hidden');
          anyVisible = true;
        }
        
        if (scrollLeft >= maxScroll - 1) {
          rightArrow.classList.add('is-hidden');
        } else {
          rightArrow.classList.remove('is-hidden');
          anyVisible = true;
        }
        
        if (anyVisible) {
          wrapper.classList.add('has-visible-arrows');
        } else {
          wrapper.classList.remove('has-visible-arrows');
        }
      }
      
      leftArrow.addEventListener('click', function() {
        pillsNav.scrollBy({ left: -scrollAmount, behavior: 'smooth' });
      });
      
      rightArrow.addEventListener('click', function() {
        pillsNav.scrollBy({ left: scrollAmount, behavior: 'smooth' });
      });
      
      pillsNav.addEventListener('scroll', updateArrowVisibility);
      
      window.addEventListener('resize', updateArrowVisibility);
      
      updateArrowVisibility();
    });
  }
  
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initPillsNavigation);
  } else {
    initPillsNavigation();
  }
  
  document.addEventListener('collection:grid-updated', function() {
    setTimeout(initPillsNavigation, 100);
  });
  
  document.addEventListener('collection:pills-updated', function() {
    initPillsNavigation();
  });
})();

document.addEventListener('click', function (event) {
  const link = event.target.closest('.collection-category-pills__pill');
  if (!link) return;
  if (!link.closest('.collection-category-pills')) return;
  if (!document.querySelector('facet-filters-form')) return;

  if (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    event.button !== 0
  ) {
    return;
  }

  var href = link.getAttribute('href');
  if (!href) return;

  let dest;
  try {
    dest = new URL(href, window.location.origin);
  } catch (e) {
    return;
  }

  if (dest.origin !== window.location.origin) return;
  if (dest.pathname !== window.location.pathname) return;

  event.preventDefault();

  link.classList.toggle('is-active');
  FacetFiltersForm.beginAjaxLoading();

  const searchParams = dest.search ? dest.search.slice(1) : '';
  FacetFiltersForm.renderPage(searchParams, null);
});

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.inputs = this.querySelectorAll('input');
    this.inputs.forEach(element =>
      element.addEventListener(
        'change',
        this.onRangeChange.bind(this)
      )
    );

    this.setMinAndMaxValues();
  }

  onRangeChange(event) {
    this.adjustToValidValues(event.currentTarget);
    this.setMinAndMaxValues();
  }

  setMinAndMaxValues() {
    const minInput = this.inputs[0];
    const maxInput = this.inputs[1];
    if (maxInput.value) minInput.setAttribute('max', maxInput.value);
    if (minInput.value) maxInput.setAttribute('min', minInput.value);
    if (minInput.value === '') maxInput.setAttribute('min', 0);
    if (maxInput.value === '')
      minInput.setAttribute('max', maxInput.getAttribute('max'));
  }

  adjustToValidValues(input) {
    const value = Number(input.value);
    const min = Number(input.getAttribute('min'));
    const max = Number(input.getAttribute('max'));

    if (value < min) input.value = min;
    if (value > max) input.value = max;
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    this.querySelector('a').addEventListener('click', event => {
      event.preventDefault();
      const form =
        this.closest('facet-filters-form') ||
        document.querySelector('facet-filters-form');
      form.onActiveFilterClick(event);
      form.clearCheckboxes();
    });
  }
}

customElements.define('facet-remove', FacetRemove);
